import React, { useState, useEffect, useRef } from "react";
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { catalogApi } from "../api/client";
import { useNavigate, useLocation } from "react-router-dom";
import { MdMyLocation } from "react-icons/md"; // 👈 location icon

const SearchBar = () => {
  const [searchText, setSearchText] = useState("");
  const [categoryId, setCategoryId] = useState([]);
  const [locationText, setLocationText] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [categories, setCategories] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
  const NOMINATIM_ZOOM = 10;
  const ALLOWED_PLACE_TYPES = new Set([
    "city",
    "town",
    "village",
    "suburb",
    "locality",
    "state",
    "district",
    "county",
  ]);
  const urlCoordsRef = useRef({ lat: null, lng: null });

  // Fetch categories
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setFiltersLoading(true);
        const cats = await catalogApi.listCategories();
        const items = Array.isArray(cats?.items) ? cats.items : [];
        setCategories(items);
      } catch (err) {
        console.error("Error fetching filters:", err);
        setError("Failed to load categories");
      } finally {
        setFiltersLoading(false);
      }
    };
    fetchFilters();
  }, []);

  // Fetch location suggestions
  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      const query = locationText.trim();
      if (!query) {
        setLocationOptions([]);
        setLocationLoading(false);
        return;
      }
      try {
        setLocationLoading(true);
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&addressdetails=1&limit=5&countrycodes=in&zoom=${NOMINATIM_ZOOM}`;
        const res = await fetch(url, {
          headers: {
            Accept: "application/json",
            "User-Agent": "OnlineIndianMarket/1.0",
            "Accept-Language": "en-IN,en;q=0.8",
          },
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        const raw = Array.isArray(data) ? data : [];
        const narrowed = raw.filter((it) =>
          ALLOWED_PLACE_TYPES.has(String(it.type || ""))
        );
        const chosen = narrowed.length > 0 ? narrowed : raw;
        const opts = chosen.map((it) => ({
          label: it.display_name,
          lat: Number(it.lat),
          lng: Number(it.lon),
        }));
        setLocationOptions(opts);
      } catch (_e) {
        // ignore aborted/failed
      } finally {
        setLocationLoading(false);
      }
    };
    const t = setTimeout(run, 250);
    return () => {
      controller.abort();
      clearTimeout(t);
    };
  }, [locationText]);

  // Populate from query params
  useEffect(() => {
    const controller = new AbortController();
    const applyFromQuery = async () => {
      const params = new URLSearchParams(location.search);
      const q = params.get("q") || "";
      const categoryIdsParam = params.get("categoryIds") || "";
      const latParam = params.get("lat");
      const lngParam = params.get("lng");

      setSearchText(q);
      const parsedCats = categoryIdsParam
        ? categoryIdsParam
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      setCategoryId(parsedCats);

      if (latParam && lngParam) {
        const latNum = Number(latParam);
        const lngNum = Number(lngParam);
        const prev = urlCoordsRef.current;
        if (prev.lat === latNum && prev.lng === lngNum) {
          return;
        }
        urlCoordsRef.current = { lat: latNum, lng: lngNum };
        setSelectedLocation((prevSel) =>
          prevSel && prevSel.lat === latNum && prevSel.lng === lngNum
            ? prevSel
            : { label: "", lat: latNum, lng: lngNum }
        );
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${encodeURIComponent(
            latNum
          )}&lon=${encodeURIComponent(
            lngNum
          )}&zoom=${NOMINATIM_ZOOM}&addressdetails=1`;
          const res = await fetch(url, {
            headers: {
              Accept: "application/json",
              "User-Agent": "OnlineIndianMarket/1.0",
              "Accept-Language": "en-IN,en;q=0.8",
            },
            signal: controller.signal,
          });
          if (res.ok) {
            const data = await res.json();
            const display =
              data?.display_name ||
              `${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`;
            setLocationText(display);
            setSelectedLocation({ label: display, lat: latNum, lng: lngNum });
          } else {
            setLocationText(`${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`);
          }
        } catch (_e) {
          setLocationText(`${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`);
        }
      } else {
        urlCoordsRef.current = { lat: null, lng: null };
      }
    };
    applyFromQuery();
    return () => controller.abort();
  }, [location.search]);

  const handleSearch = async () => {
    setError("");
    try {
      const params = new URLSearchParams();
      if (searchText.trim()) params.set("q", searchText.trim());
      if (categoryId.length > 0)
        params.set("categoryIds", categoryId.join(","));
      if (
        selectedLocation &&
        typeof selectedLocation.lat === "number" &&
        typeof selectedLocation.lng === "number"
      ) {
        params.set("lat", String(selectedLocation.lat));
        params.set("lng", String(selectedLocation.lng));
      }
      navigate(`/services?${params.toString()}`);
    } catch (err) {
      console.error("Search error:", err);
      setError("Search failed. Please try again.");
    }
  };

  // 👇 Handle current location button
  const handleGetCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setLocationLoading(true);

    const geolocationOptions = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds timeout
      maximumAge: 0, // Don't use cached position
    };

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${encodeURIComponent(
            lat
          )}&lon=${encodeURIComponent(
            lng
          )}&zoom=${NOMINATIM_ZOOM}&addressdetails=1`;
          const res = await fetch(url, {
            headers: {
              Accept: "application/json",
              "User-Agent": "OnlineIndianMarket/1.0",
              "Accept-Language": "en-IN,en;q=0.8",
            },
          });
          let label = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
          if (res.ok) {
            const data = await res.json();
            if (data?.display_name) label = data.display_name;
          }
          setLocationText(label);
          setSelectedLocation({ label, lat, lng });
        } catch (err) {
          console.error("Reverse geocoding failed:", err);
          setError("Failed to fetch location");
        } finally {
          setLocationLoading(false);
        }
      },
      (err) => {
        console.error("Geolocation error:", err);
        setError("Unable to retrieve your location");
        setLocationLoading(false);
      }
    );
  };

  return (
    <div className="w-full lg:w-4/5 max-w-5xl mx-auto poppins-regular">
      <div
        className="
        bg-white rounded-md px-2 py-2 sm:px-4 sm:py-3
        grid grid-cols-2 gap-3 items-center
        lg:flex lg:items-center lg:gap-4
      "
      >
        {/* 🔵 Search Input (Mobile full width / Web inline) */}
        <div className="col-span-2  lg:flex-1">
          <TextField
            fullWidth
            label="Search"
            placeholder="What are you looking for?"
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* 🟩 Categories */}
        <div className="col-span-1 lg:w-56">
          <FormControl fullWidth>
            <InputLabel>Categories</InputLabel>
            <Select
              multiple
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              input={<OutlinedInput label="Categories" />}
              renderValue={(selected) =>
                categories
                  .filter((cat) => selected.includes(cat._id))
                  .map((cat) => cat.name)
                  .join(", ")
              }
              MenuProps={MenuProps}
            >
              {filtersLoading ? (
                <MenuItem disabled>Loading categories...</MenuItem>
              ) : categories.length > 0 ? (
                categories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    <Checkbox checked={categoryId.includes(cat._id)} />
                    <ListItemText
                      className="text-xs md:text-base"
                      primary={cat.name}
                    />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No categories available</MenuItem>
              )}
            </Select>
          </FormControl>
        </div>

        {/* 🟦 Location */}
        <div className="col-span-1 lg:w-56">
          <Autocomplete
            freeSolo
            options={locationOptions}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            inputValue={locationText}
            value={selectedLocation}
            onInputChange={(_e, value) => setLocationText(value)}
            onChange={(_e, value) => setSelectedLocation(value || null)}
            loading={locationLoading}
            loadingText="Loading locations..."
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location"
                placeholder="City, state or locality"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {locationLoading && (
                        <CircularProgress color="inherit" size={16} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </div>

        {/* 🔴 Buttons (Mobile full width / Web inline) */}
        <div className="col-span-2 flex items-center gap-2 lg:w-auto">
          <button
            onClick={handleSearch}
            className="
            w-full lg:w-auto bg-blue-600 hover:bg-blue-700 text-white
            px-6 py-3 rounded-lg text-lg uppercase
            bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700
          "
          >
            Search
          </button>

          <button
            onClick={handleGetCurrentLocation}
            className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600"
            title="Use my location"
          >
            <MdMyLocation size={24} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 mb-3 p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
